"use client";
import { CaretDown, X } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";

const LeadForm = ({ onClose }) => {
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEsc);
    return () => {
      window.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "auto";
    };
  }, [onClose]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const validate = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[6-9]\d{9}$/;

    if (!formData.fullName.trim()) newErrors.fullName = "Full Name is required.";
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required.";
    else if (!phoneRegex.test(formData.phone)) newErrors.phone = "Invalid phone number.";
    if (!formData.email.trim()) newErrors.email = "Email is required.";
    else if (!emailRegex.test(formData.email)) newErrors.email = "Invalid email address.";

    return newErrors;
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  const validationErrors = validate();
  if (Object.keys(validationErrors).length > 0) {
    setErrors(validationErrors);
    return;
  }

  setIsSubmitting(true);

  try {
    const res = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/enquiry/contact-us`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        data: {
          name: formData.fullName,
          number: formData.phone,
          email: formData.email,
        },
      }),
    });

    if (!res.ok) throw new Error("API request failed");

    toast.success("We will get in touch with you");
    setFormData({ fullName: "", phone: "", email: "" });
    onClose();
  } catch (error) {
    toast.error("Something went wrong !");
    console.error("Error submitting form:", error);
  } finally {
    setIsSubmitting(false);
  }
};

  return (
    <AnimatePresence>
      <motion.div
        key="backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed top-0 md:top-12 inset-0 z-50 flex items-center justify-center px-[20px] md:px-0"
        onClick={onClose}
        style={{
          background: "linear-gradient(to bottom, transparent, rgba(0,0,0,1))",
        }}
      >
        <motion.div
          key="modal"
          onClick={(e) => e.stopPropagation()}
          initial={{ opacity: 0, scale: 0.9, y: -40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 40 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          className="bg-white rounded-none md:rounded-md w-full max-w-xl relative shadow-lg px-[16px] py-[32px] md:p-[32px] flex flex-col gap-[24px] md:gap-[32px]"
        >
          <div className="flex flex-row-reverse justify-between items-center border-b border-[#E4E4E4] pb-3">
            <button onClick={onClose} className="text-gray-600 hover:text-black cursor-pointer">
              <X size={24} />
            </button>
            <h2 className="text-[28px] md:text-[27px] font-[600]">Contact Us</h2>
          </div>

          <form className="space-y-[32px]" onSubmit={handleSubmit}>
            <div>
              <label className="block text-[12px] font-[600]">Full Name</label>
              <input
                name="fullName"
                type="text"
                placeholder="Your full name"
                value={formData.fullName}
                onChange={handleChange}
                className={`w-full p-[16px] bg-[#EFEFEF] text-[#333] rounded-[4px] focus:outline-none ${
                  errors.fullName ? "border border-red-500" : ""
                }`}
              />
              {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
            </div>

            <div>
              <label className="block text-[12px] font-[600]">Contact Number</label>
              <div
                className={`flex items-center gap-[8px] w-full p-[16px] bg-[#EFEFEF] rounded-[4px] text-[#333] ${
                  errors.phone ? "border border-red-500" : ""
                }`}
              >
                <span>+91</span>
                <CaretDown size={14} />
                <span>|</span>
                <input
                  name="phone"
                  type="text"
                  placeholder="9876543210"
                  value={formData.phone}
                  onChange={handleChange}
                  className="bg-transparent focus:outline-none w-full"
                />
              </div>
              {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
            </div>

            <div>
              <label className="block text-[12px] font-[600]">Email</label>
              <input
                name="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                className={`w-full p-[16px] bg-[#EFEFEF] text-[#333] rounded-[4px] focus:outline-none ${
                  errors.email ? "border border-red-500" : ""
                }`}
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>

            <div className="flex justify-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full md:w-auto bg-[#0D6FEA] text-white py-[8px] px-[16px] rounded-[4px] font-[400] cursor-pointer ${
                  isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {isSubmitting ? "Submitting..." : "SUBMIT"}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LeadForm;
